import { useRef, useReducer, useState, useCallback, Key } from 'react';
import { useDispatch } from 'react-redux';

import classNames from 'classnames';

import { APP_NAME } from "@web-solutions/core/constants/general";

import { setOrderPending } from '@web-solutions/core/store/billing/actions';

import { PaymentSystem } from '../constants';
import validate from '../utils/validation';
import usePayment from '../hooks/use-payment';
import { handleSubmitStripe } from '../stripe/card-form-methods';
import { handleSubmitRecurly } from '../recurly/card-form-methods';


const REQUIRED_RECURLY_FIELDS = ['number', 'month', 'year'];

const getSubmitButtonStyle = (appName: string) => {
  if(appName === 'Brainary') return {
    "background": "#843EF2",
    "border-radius": "8px",
    '.title': { 
      "font-weight": "600",
      "font-size": "18px",
      "line-height": "normal",
      "letter-spacing": "0px",
      "color": "#fff",
      'text-transform': 'capitalize',
    },
  }
  return {
    background: '#843EF2',
    "border-radius": '50px',
    '.title': {
      'font-size': '22px',
      'font-family': 'Open Sans, sans-serif',
      'font-weight': '500',
      'color': '#fff',
      'letter-spacing': '-0.57px',
      'text-shadow': '0 1px 2px rgba(0, 0, 0, 0.2)',
      'text-transform': 'capitalize',
    },
  }
}

const formReducer = (state: { [x: string]: any; }, e: { name?: any; touch?: any; value?: any; }) => {
  const { value } = e;
  return {
    ...state,
    [e.name]: {
      ...state[e.name],
      value,
      isValid: validate(value, e.name, 2),
      isTouched: e.touch,
    },
  };
};


export const useCardFormMethods = ({
  paymentSystem,
  product,
  onSubmit,
  onChange,
  onSuccess,
  onError,
  onApplePaySubmit,
  onGooglePaySubmit,
  fieldClassName,
  additionalFields,
  children,
  onValidateChildren,
  onSaveTokenOneTime,
  styles
}: any) => {
      const formRef = useRef(null);
      const dispatch = useDispatch()
    
      const [submitted, setSubmitted] = useState(false);
      const [isInvalidCardForm, setInvalidCardForm] = useState(false);
    
      const submitButtonStyle = getSubmitButtonStyle(APP_NAME as string);
      const payment = usePayment();
    
      const initialState = Object.keys(additionalFields).reduce(
        (acc, key) => {
          if (key === 'cardholder') {
            return { ...acc };
          }
          return {
            ...acc,
            [key]: {
              value: '',
              ...additionalFields[key],
              isValid: validate(additionalFields[key].value || '', key),
              isTouched: false,
            },
          }
        },
        {}
      );
    
      const [fields, setFormData]: any = useReducer(formReducer, initialState);
    
      const handleInputChange = useCallback((e: { preventDefault: () => void; target: any; }) => {
        e.preventDefault();
        const { value, name } = e.target;
    
        setFormData({
          name,
          value,
          touch: true,
        });
    
        if (typeof onChange === 'function') onChange(e);
      }, []);
    
      const validateForm = () => {
        Object.keys(fields).map((key) => {
          setFormData({
            name: key,
            value: fields[key].value,
            touch: true,
          });
          return key;
        });
      };
    
      const handleSubmitError = (error: { fields: any[]; type: string; code: string; }) => {
        console.log('[SUBMIT ERROR]', error);
    
        if (error.fields) {
          error.fields.map((key: string) => {
            if (REQUIRED_RECURLY_FIELDS.includes(key) && !isInvalidCardForm) {
              setInvalidCardForm(true);
            }
    
            return key;
          });
        }
    
        if (error.type === 'validation_error' || error.code === 'validation') {
          setInvalidCardForm(true);
        }
    
        return true;
      };

      const handleSubmit = async (e: any) => {
        e.preventDefault?.();
        dispatch(setOrderPending(true))
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 1000);
        const { id } = product;
    
        const formData: any = {
          price_id: id,
          email: fields.email?.value,
          first_name: (document.querySelector('input[name=first_name]') as any)?.value,
          last_name: (document.querySelector('input[name=last_name]') as any)?.value,
          method: 'card',
          paymentSystem,
        };
    
        validateForm();
    
        try {
          if (!navigator.onLine) {
            onError()
            throw new Error('Network error')
          }

          if (paymentSystem === PaymentSystem.RECURLY) {
            await handleSubmitRecurly({
              payment, 
              formRef, 
              formData, 
              onSaveTokenOneTime, 
              handleSubmitError, 
              onSubmit,
              children,
              onValidateChildren
          })
          } else if (paymentSystem === PaymentSystem.STRIPE) {
            await handleSubmitStripe({
              formData, 
              fields, 
              payment, 
              handleSubmitError, 
              product, 
              onSubmit
            })
          } else if (paymentSystem === PaymentSystem.SOLIDGATE || paymentSystem === PaymentSystem.TRUEGATE) {
            onSubmit(formData);
          }
        } catch (error) {
          console.log('[SUBMIT ERROR]', error);
        } finally {
          dispatch(setOrderPending(false))
        }
      };
    
      const handleApplePaySubmit = (e: any) => {
        onApplePaySubmit({
          ...e,
          paymentSystem,
        });
      };
    
      const handleGooglePaySubmit = (e: any) => {
        onGooglePaySubmit({
          ...e,
          paymentSystem,
        });
      };
    
      const handleCardElementChange = (e: any) => {
        if (isInvalidCardForm) {
          setInvalidCardForm(false);
        }
    
        if (typeof onChange === 'function') {
          onChange(e);
        }
      };
    
      const handleSuccess = (e: any) => {
        onSuccess({
          ...e,
          paymentSystem,
        });
      };
    
      const handleError = (err: any) => {
        onError({
          ...(err || {}),
          paymentSystem,
        });
      };
    
      const renderField = (key: any, attributes: any) => {
        const {
          label,
          isValid,
          isTouched,
          value,
          type,
          placeholder = '',
        } = attributes;
        return (
          <div
            key={key}
            className={classNames(
              styles.field,
              {
                invalid: !isValid && isTouched,
              },
              fieldClassName
            )}
          >
            <label htmlFor={`additional-field-${key}`}>{label}</label>
            <input
              id={`additional-field-${key}`}
              data-recurly={key}
              name={key}
              placeholder={placeholder}
              value={value}
              type={type}
              onChange={handleInputChange}
            />
          </div>
        );
      };
    
      const renderFields = () => {
        const fieldsArray = Object.keys(fields);
    
        if (!fieldsArray.length) {
          return null;
        }
    
        return fieldsArray.map((key) => renderField(key, fields[key]));
      };
    
    
  return {
    handleSubmit,
    formRef,
    paymentSystem,
    isInvalidCardForm,
    submitted,
    submitButtonStyle,
    handleSuccess,
    handleError,
    handleCardElementChange,
    handleApplePaySubmit,
    handleGooglePaySubmit,
    renderFields
  }
}